import { type FunctionComponent } from 'react';
import {
	ACCOUNT_LINK,
	ACCOUNT_LOGIN_LINK,
	ACCOUNT_PROJECTS_LINK,
	ACCOUNT_SALES_REP_LINK,
	ACCOUNT_STORE_CREDIT_LINK,
	ORDER_STATUS_LINK,
	ORDER_SUMMARY_LINK,
	ORDER_TRACK_LINK,
	SET_UP_RETURN_LINK
} from '../../../../constants/links';
import { useCustomerLogout } from '../../../../hooks/apollo/authentication/authentication.hooks';
import { useCustomer } from '../../../../hooks/apollo/customer/customer.hooks';
import { StyledButton, TextButton } from '../../../buttons';
import { Link, type LinkProps } from '../../../common-components/link/link.component';
import OmniProBadgeIconSvg from '../../../svgs/omni-pro-badge-icon.svg';
import { proBadge } from './account-section.css';

type AccountLink = LinkProps & { description: string };

const accountLinks: AccountLink[] = [
	{
		url: ACCOUNT_LINK,
		description: 'Account'
	},
	{
		url: ACCOUNT_PROJECTS_LINK,
		description: 'Projects'
	},
	{
		url: ORDER_STATUS_LINK,
		description: 'Orders & Returns'
	},
	{
		url: ORDER_TRACK_LINK,
		description: 'Track Order'
	},
	{
		url: SET_UP_RETURN_LINK,
		description: 'Set Up Return'
	},
	{
		url: ORDER_SUMMARY_LINK,
		description: 'Order Summary'
	}
];

const salesRepLink: AccountLink = {
	url: ACCOUNT_SALES_REP_LINK,
	description: 'My Pro Rep'
};

const storeCreditLink: AccountLink = {
	url: ACCOUNT_STORE_CREDIT_LINK,
	description: 'Store Credit'
};

export const AccountSection: FunctionComponent<{ closeNavigation: () => void }> = ({ closeNavigation }) => {
	const { hasAccount, isPro, data: { customer } = {} } = useCustomer();

	const { logout } = useCustomerLogout();

	const onLogoutClick = () => {
		void logout();
		closeNavigation();
	};

	function customizeLinks(links: AccountLink[]) {
		let customizedLinks = [...links];

		if (hasAccount) {
			const { salesRepresentative, storeCredit } = customer || {};
			if (salesRepresentative) {
				customizedLinks.push(salesRepLink);
			}
			if (storeCredit) {
				customizedLinks.push(storeCreditLink);
			}
		} else {
			customizedLinks = links;
		}
		return customizedLinks;
	}

	return (
		<nav className="mt3 flex flex-column justify-center tc b theme-grey-darker" role="navigation" aria-label="account section">
			{customer && hasAccount ? (
				<div className="bb b--theme-grey-light pb2">
					<div className="mb1 b f6">Hello, {customer.profile.firstName}!</div>
					<div className="f7">
						<div className="flex flex-column items-center center">
							<div className="mt1 normal">Edit Your Profile</div>
							{isPro && <OmniProBadgeIconSvg className={`${proBadge} theme-pro`} data-testid="OmniProBadgeIconSvg" />}
						</div>
					</div>
				</div>
			) : (
				<Link url={ACCOUNT_LOGIN_LINK} className="w-100">
					<StyledButton>My Account / Login</StyledButton>
				</Link>
			)}
			{customizeLinks(accountLinks).map(({ url, description }, index) => (
				<Link key={index} url={url} className={'pv2 mt2'} color={'grey-darker'} underline={false}>
					{description}
				</Link>
			))}
			{customer && hasAccount && (
				<TextButton className="pv2 mt2" color="grey-darker" onClick={onLogoutClick} underline={false}>
					Log Out
				</TextButton>
			)}
		</nav>
	);
};
